import { CircleLoader } from "@/components/shared/loaders";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useEffect } from "react";
import { z } from "zod";
import { FieldErrors, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { FetchMainOrganizationsWithNoBillingDocument, FetchMainOrganizationsWithNoBillingQuery, FetchMainOrganizationsWithNoBillingQueryVariables, FetchPricingWithProductDetailsDocument, FetchPricingWithProductDetailsQuery, FetchPricingWithProductDetailsQueryVariables } from "@/graphql/__generated__/graphql";
import { ComboboxInputField, FormattedNumberInputField, InputField } from "@/components/shared";
import useCreateBillingMutationHook from "@/hooks/mutations/useCreateBillingMutation";
import { t as translate } from "i18next";
import { nonEmptyString, nonNegativeInteger } from "@/utils/form";
import { useQuery } from "@apollo/client/react";

const internationalEmailRegex =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

const defaultFormValues = {
  organizationId: undefined,
  billingEmail: "",
  customerName: "",
  pricingId: "",
  vatNumber: "",
  daysUntilDue: 14,
};

const formSchema = z.object({
  organizationId: nonNegativeInteger(translate("modals:createbilling.inputs.organization.label")),
  billingEmail: z
    .string()
    .refine((val) => internationalEmailRegex.test(val), {
      message: translate("schemas:createuser.invalidemail"),
    }),
  customerName: nonEmptyString(translate("modals:createbilling.inputs.customername.label")),
  pricingId: nonEmptyString(translate("modals:createbilling.inputs.product.label")),
  vatNumber: nonEmptyString(translate("modals:createbilling.inputs.vatnumber.label")),
  daysUntilDue: nonNegativeInteger(translate("modals:createbilling.inputs.daysuntildue.label")),
});

interface Props {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

function CreateBillingDialog(props: Props) {
  const { open, onOpenChange } = props;
  const { t } = useTranslation(["modals"]);

  const { data: mainOrganizationsWithNoContract, loading: mainOrganizationsWithNoContractLoading, refetch: refetchOrganizations } = useQuery<FetchMainOrganizationsWithNoBillingQuery, FetchMainOrganizationsWithNoBillingQueryVariables>(FetchMainOrganizationsWithNoBillingDocument,{
  });
  const { data: pricingWithProductDetails, loading: pricingWithProductDetailsLoading } = useQuery<FetchPricingWithProductDetailsQuery, FetchPricingWithProductDetailsQueryVariables>(FetchPricingWithProductDetailsDocument,{
    fetchPolicy: "cache-and-network"
  });

  const { createBillingMutation, loading: createBillingLoading } = useCreateBillingMutationHook();

  const mainOrganizationsWithNoContractOptions = mainOrganizationsWithNoContract?.fetchMainOrganizationsWithNoBilling.map((organization) => ({
    label: organization.name,
    value: organization.id
  }));

  const pricingWithProductDetailsOptions = pricingWithProductDetails?.fetchPricingWithProductDetails.map((pricing) => ({
    label: `${pricing.productName} - ${pricing.unitAmount} ${pricing.currency.toUpperCase()} / ${pricing.interval}`,
    value: pricing.priceId
  }));

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema) as Resolver<z.infer<typeof formSchema>>,
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  // Reset form and refetch data when modal opens
  useEffect(() => {
    if (open) {
      form.reset(defaultFormValues);
      refetchOrganizations();
    }
  }, [open, form, refetchOrganizations]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    createBillingMutation({
      variables: {
        input: data
      },
      onCompleted: () => {
        // Close modal on successful creation
        onOpenChange(false);
      }
    });
  };

  const onInvalid = (errors: FieldErrors<z.infer<typeof formSchema>>) => console.error(errors);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="select-none min-w-fit max-w-[90vw] lg:max-w-fit overflow-y-auto max-h-[90vh] h-full md:h-fit">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6">
            <DialogHeader className="text-left">
              <DialogTitle>{t("createbilling.title")}</DialogTitle>
              <DialogDescription>{t("createbilling.description")}</DialogDescription>
            </DialogHeader>
            {/* form inputs */}
            <div className="flex flex-col gap-4">
              <InputField
                label={t("createbilling.inputs.customername.label")}
                name="customerName"
                placeholder={t("createbilling.inputs.customername.placeholder")}
                form={form}
                className="w-full"
                disabled={false}
              />

              <InputField
                label={t("createbilling.inputs.billingemail.label")}
                name="billingEmail"
                placeholder={t("createbilling.inputs.billingemail.placeholder")}
                form={form}
                className="w-full"
                disabled={false}
              />

              <ComboboxInputField
                form={form}
                name="organizationId"
                label={t("createbilling.inputs.organization.label")}
                options={mainOrganizationsWithNoContractOptions ?? []}
                disabled={mainOrganizationsWithNoContractLoading}
                loading={mainOrganizationsWithNoContractLoading}
                className="w-full"
              />

              <ComboboxInputField
                form={form}
                name="pricingId"
                label={t("createbilling.inputs.product.label")}
                options={pricingWithProductDetailsOptions ?? []}
                disabled={pricingWithProductDetailsLoading}
                loading={pricingWithProductDetailsLoading}
                className="w-full"
              />

              <div className="flex gap-4">
                <InputField
                  label={t("createbilling.inputs.vatnumber.label")}
                  name="vatNumber"
                  placeholder={t("createbilling.inputs.vatnumber.placeholder")}
                  form={form}
                  className="w-full"
                  disabled={false}
                />

                <FormattedNumberInputField
                  control={form.control}
                  name="daysUntilDue"
                  label={t("createbilling.inputs.daysuntildue.label")}
                  placeholder={t("createbilling.inputs.daysuntildue.placeholder")}
                  additionalSuffix={t("createbilling.inputs.daysuntildue.suffix")}
                  isInteger
                  isStandalone
                  style="decimal"
                  spaceBetweenSuffix={true}
                  className="w-full"
                />
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={createBillingLoading}>
                  {t("common:buttons.cancel")}
                </Button>
              </DialogClose>
              <Button variant="default" type="submit" disabled={createBillingLoading} className="gap-2">
                <CircleLoader visible={createBillingLoading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateBillingDialog;
